import type { AiRedaction, EventExplanation } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { explainEvent, getAiStatus, getExplanation } from '../api/ai.js';
import { formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';

/**
 * An event explained by the AI layer (section 13; P9.2).
 *
 * Shown when one has been made, to anyone who can see the event; the button is
 * an operator's, and only while a model is set up for it. What the model wrote is
 * text — React escapes it — and the card says plainly that it is advice from a
 * model, which can be wrong.
 */

const SEVERITY_TEXT: Record<EventExplanation['severity'], string> = {
  agree: 'The reported severity looks right.',
  lower: 'This looks less serious than its severity says.',
  higher: 'This looks more serious than its severity says.',
};

const MASK_TEXT: Record<AiRedaction, string> = {
  ips: 'addresses',
  usernames: 'user names',
  internal_hostnames: 'server names',
  paths: 'paths',
};

export function ExplanationCard({
  eventId,
  canAsk,
}: {
  eventId: string;
  canAsk: boolean;
}): ReactNode {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ['ai-status'], queryFn: getAiStatus });
  const stored = useQuery({
    queryKey: ['explanation', eventId],
    queryFn: () => getExplanation(eventId),
  });
  const [error, setError] = useState<string | null>(null);
  const ask = useMutation({
    mutationFn: () => explainEvent(eventId),
    onSuccess: (view) => {
      queryClient.setQueryData(['explanation', eventId], view);
    },
    onError: (caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'The model did not answer.');
    },
  });

  const available = status.data?.tasks.explain_event === true;
  const view = stored.data ?? null;
  if (view === null && !(available && canAsk)) return null;

  return (
    <Card
      title="Explanation"
      description="Advice from a language model about this event. It can be wrong: check before acting on it."
      aside={
        canAsk && available ? (
          <Button
            busy={ask.isPending}
            busyLabel="Asking…"
            onClick={() => {
              setError(null);
              ask.mutate();
            }}
          >
            {view === null ? 'Explain' : 'Explain again'}
          </Button>
        ) : undefined
      }
    >
      {error !== null && <Alert tone="danger">{error}</Alert>}
      {view === null ? (
        <p className="muted">
          Ask a model what this event means, what probably caused it and what to check next. The
          event and counts of what else happened around it are sent, masked as the AI settings say.
        </p>
      ) : (
        <div className="stack">
          <p>{view.explanation.summary}</p>
          {view.explanation.likelyCause !== '' && (
            <p>
              <strong>Likely cause.</strong> {view.explanation.likelyCause}
            </p>
          )}
          <p>
            <strong>{SEVERITY_TEXT[view.explanation.severity]}</strong>{' '}
            {view.explanation.severityReason}
          </p>
          {view.explanation.nextSteps.length > 0 && (
            <>
              <strong>What to check</strong>
              <ul>
                {view.explanation.nextSteps.map((step, index) => (
                  // Steps can repeat word for word; their place in the list is what they are.
                  <li key={`${String(index)}:${step}`}>{step}</li>
                ))}
              </ul>
            </>
          )}
          <p className="cell-sub">
            Confidence {view.explanation.confidence} · {view.providerName} · {view.model} ·{' '}
            {formatRelative(view.createdAt)}
            {view.createdByEmail !== null && `, asked by ${view.createdByEmail}`}
            {view.redacted.length > 0 &&
              ` · sent with ${view.redacted.map((each) => MASK_TEXT[each]).join(', ')} masked`}
          </p>
        </div>
      )}
    </Card>
  );
}
